/**
 * Search Slice - Zustand Store
 * Manages address search settings and recent search history
 *
 * Replaces:
 * - useSearchSettings hook (localStorage + useState)
 * - useRecentSearches hook (localStorage + useState)
 *
 * Benefits:
 * - Centralized global state (no prop drilling)
 * - Automatic persistence with Zustand persist middleware
 * - Zero localStorage boilerplate
 */

import { PlaceType } from '../../components/map/AddressSearchBar/types';
import type { SearchHistoryItem } from '../../utils/searchHistory';

const MAX_RECENT_SEARCHES = 10;

export interface SearchSlice {
  search: {
    // Search settings (persisted)
    selectedPlaceTypes: Set<PlaceType>;
    useLocationPriority: boolean;

    // Recent searches (persisted)
    recentSearches: SearchHistoryItem[];
  };

  // Actions
  setPlaceTypes: (types: Set<PlaceType>) => void;
  setLocationPriority: (enabled: boolean) => void;
  addRecentSearch: (search: SearchHistoryItem) => void;
  clearRecentSearches: () => void;
}

export const createSearchSlice = (
  set: (fn: (state: SearchSlice) => void) => void,
  _get: () => unknown,
  _store: unknown
) => ({
  search: {
    // Default: all place types selected
    selectedPlaceTypes: new Set(Object.values(PlaceType)),
    useLocationPriority: true,
    recentSearches: [],
  },

  setPlaceTypes: (types: Set<PlaceType>) =>
    set((state) => {
      state.search.selectedPlaceTypes = types;
    }),

  setLocationPriority: (enabled: boolean) =>
    set((state) => {
      state.search.useLocationPriority = enabled;
    }),

  addRecentSearch: (search: SearchHistoryItem) =>
    set((state) => {
      // Check if this search already exists (by display_name)
      const existingIndex = state.search.recentSearches.findIndex(
        (s) => s.display_name === search.display_name
      );

      // Remove existing entry if found
      if (existingIndex !== -1) {
        state.search.recentSearches.splice(existingIndex, 1);
      }

      // Add new search at the beginning
      state.search.recentSearches.unshift(search);

      // Keep only the most recent searches
      state.search.recentSearches = state.search.recentSearches.slice(
        0,
        MAX_RECENT_SEARCHES
      );
    }),

  clearRecentSearches: () =>
    set((state) => {
      state.search.recentSearches = [];
    }),
});
